import type { JSONSchemaNode } from '@/lib/api/types';
import {
  CARD_COLOR,
  CARD_ICON,
  RATE_ROLE,
  RATE_SCALE,
  COURSE_FORMAT,
  COURSE_STATUS,
  COURSE_STATUS_OVERRIDE,
  DOCUMENT_CATEGORY,
  HERO_LINE,
  LOGO_SIZE,
  PARTNER_KIND,
  FUNDRAISING_YEAR_STATUS,
  MEDIA_TYPE,
  METRIC_COMPUTE,
  METRIC_KIND,
  METRIC_MODE,
  OFFERING,
  ORIGIN_KIND,
  ORIGIN_TIER,
  TESTIMONIAL_KIND,
  PROJECT_INSPIRED_BY,
  SURVEY_UNIT,
  WORK_CATEGORY,
  enumLabels,
  type VocabOption,
} from './vocab';

/**
 * Content-collection registry. One config-driven engine serves every collection;
 * adding a collection is a single entry here. `docSchema` is the console-shipped,
 * per-collection doc form definition — but it is only the **offline fallback**: the
 * backend serves the authoritative schema at `/admin/collection-schemas` and wins at
 * runtime (SchemaForm renders whatever JSON Schema it gets). Keep this in sync with
 * the backend `docschema` so the offline editor matches the live one.
 *
 * THIS CONSOLE IS SVET'S. PHASE 01 of PLAN_client-feedback-round-2 removed the
 * eight collections this console inherited from the agency site it was forked
 * from (field-notes, services, case-studies, packages, clients, team-members,
 * testimonials, site-assets), and `be-svet` migration 0021 dropped their tables.
 * PHASE 02 registered Svet's four editorial collections (courses, works,
 * svet-projects, press-items); PHASE 03 registered the eleven key-addressed
 * registers (partners, testimonials, documents, impact-metrics, survey-results,
 * research-citations, student-origins, pricing-tiers, teachers, team-members and
 * the fundraising singleton).
 *
 * SVET PUBLISHES ENGLISH ONLY, so its doc fields are PLAIN strings, not `{en,ar}`
 * pairs: `textField` / `richTextField`, not `i18nField`. An i18n field renders an
 * EN/AR tab pair with a permanently incomplete "AR ·" chip, which on an
 * English-only site is a form asking every editor for copy nobody will publish.
 * The record's own title stays bilingual — that is the shared item shape, not a
 * per-collection choice.
 */
export interface FacetDef {
  key: string;
  label: string;
  /**
   * Facet kind. A facet is a field the PUBLIC list filters on server-side
   * (`doc->>'<key>'`), and it must be indexed in the collection's migration —
   * declaring one here that the backend does not index is a silent seq scan.
   */
  type: 'text' | 'boolean' | 'enum' | 'ref-slug';
  /** Static options for `enum` facets. */
  options?: VocabOption[];
  /** Collection key for `ref-slug` facets. */
  refCollection?: string;
}

export interface CollectionConfig {
  /** route segment under /admin and the API path segment (e.g. `courses`). */
  key: string;
  /**
   * The backend's singular entity name (`content.Config.Entity`, e.g. `course`).
   * Search hits, audit rows and revisions are keyed by it, so the console derives
   * its entity↔route mapping from here rather than from a second hand-kept table.
   */
  entity: string;
  /** i18n nav key. */
  navKey: string;
  /** Display label (EN fallback for chrome). */
  label: string;
  /** Singular i18n label key — used for the "New {singular}" editor heading. */
  singularLabel: string;
  identType: 'slug' | 'key';
  /**
   * The key the BACKEND calls this collection where that differs from `key`.
   *
   * `GET /admin/dashboard` reports its `content` map by table name — `press_items`,
   * `svet_projects`, `svet_documents` — and the console's own key is a route segment.
   * Underscores-to-hyphens closes the gap for fifteen of the sixteen, and `svet_documents`
   * is the one it cannot: this collection's route is `/content/documents`, because the
   * console calls it Documents, and the table is `svet_documents`. The dashboard's card
   * for it was therefore unlabelled (`svet documents`, humanized from the raw key) and
   * not a link at all, on the one page whose job is to be the way in (QA F-11).
   *
   * Stated here rather than special-cased at the call site so that the mapping lives with
   * the collection definition and one lookup — `collectionForApiKey` — serves every
   * consumer. Renaming `key` to `svet-documents` instead would have been the cheaper edit
   * and the wrong one: `key` is in the URL, so every existing link and bookmark to
   * `/content/documents` would break.
   */
  apiKey?: string;
  hasSeo: boolean;
  /** When true, items can only be edited — no create or delete (a fixed register of records). */
  editOnly?: boolean;
  /**
   * One record, edited in place (implies `editOnly`). The nav entry opens that record
   * instead of a one-row list; the list still shows when it is empty or somehow has more.
   */
  singleton?: boolean;
  facets: FacetDef[];
  /** JSON-Schema for the bilingual `doc` form. */
  docSchema: JSONSchemaNode;
  /**
   * Adds the list's "Applies to" column (round 7, B-R7-01): which offering or courses each
   * row prices. Pricing tiers only today — the column reads a tier's `offering` and `courses`.
   */
  appliesToColumn?: boolean;
  /**
   * Adds the list's "Status on the site" column (round 8, D-143): a course's status as
   * be-svet derives it from the dates, and where it came from — read from the API's
   * `effective_status`/`status_source`, never recomputed here.
   */
  effectiveStatusColumn?: boolean;
  /**
   * Adds the list's "Country" column (round 9 phase 08): a work's countries with the flags
   * the website draws, or its free-text place. Works only — the column reads `countries`
   * and `country_text`.
   */
  countryColumn?: boolean;
  /**
   * The list offers "Reorder" (round 8, R8-34): drag the whole collection into the order
   * the site shows it, saved through `PUT /admin/{key}/order`. Must match be-svet's
   * `content.Config.Orderable` — on any other collection that route does not exist.
   */
  orderable?: boolean;
  /**
   * The list also offers "Order by place" (round 8, D-151): one hand-made order per
   * place the item is ticked for (`doc.placements`), saved through
   * `PUT /admin/{key}/order/{placement}`. Must match be-svet's `content.Config.Placements`.
   */
  placements?: boolean;
  /**
   * The collection whose records replaced this one's (round 9, D-172: `teachers` and
   * `team-members` → `people`). The list and the record stay readable for one release,
   * behind a notice pointing there, with every write hidden; the menu drops it. Must match
   * be-svet's `content.Config.ReplacedBy`, which answers each write with `405 read_only`.
   */
  replacedBy?: string;
  /**
   * A new record's key is made from its English title until the editor types one, as a
   * slug collection's slug is (round 9: a person's key is their name, "Jack Schaaf" →
   * `jack-schaaf`). Must match be-svet's `content.Config.KeyFromTitle`.
   */
  keyFromTitle?: boolean;
}

const I18N: JSONSchemaNode = { $ref: '#/$defs/I18n' };
const I18N_RICH: JSONSchemaNode = { $ref: '#/$defs/I18nRich' };

/**
 * `x-visible-when` for a testimonial's fields (round 8): the speaker fields mean
 * nothing on a statistic, and the caption means nothing on a quote. A console hint
 * only; validation ignores it, as on the served schema.
 */
const SPEAKER = { kind: ['student', 'parent', 'partner'] };
const STATISTIC = { kind: ['statistic'] };
/** A partner's quote only (round 11): the partner picker means nothing on anyone else's. */
const PARTNER_SPEAKER = { kind: ['partner'] };

/**
 * `x-visible-when` / `x-visible-when-any` for a person's fields (round 9, D-172): each
 * role tick reveals its own fields; the board fields serve a board member OR an advisor.
 */
const COFOUNDER = { is_cofounder: [true] };
const TEACHER = { is_teacher: [true] };
const BOARD_OR_ADVISOR: Record<string, unknown[]>[] = [{ is_board: [true] }, { is_advisor: [true] }];

const DEFS = {
  I18n: { type: 'object', properties: { en: { type: 'string' }, ar: { type: 'string' } } },
  I18nRich: { type: 'object', properties: { en: { type: 'string' }, ar: { type: 'string' } } },
} as const;

export const SCHEMA_DEFS = DEFS as unknown as Record<string, JSONSchemaNode>;

/** A plain, single-locale text leaf — the backend `str()` shape. */
export const textField = (title: string, hint?: string): JSONSchemaNode => ({
  type: 'string',
  title,
  ...(hint ? { 'x-hint': hint } : {}),
});

/**
 * A third party's identifier whose name ends in `_id` (the Givebutter account and
 * widget IDs). Without `x-widget: 'text'` the console reads a `*_id` leaf as a
 * media reference and offers the asset picker, so the ID could not be typed.
 */
export const externalIdField = (title: string, hint?: string): JSONSchemaNode => ({
  ...textField(title, hint),
  'x-widget': 'text',
});

/**
 * A plain long-form leaf — the backend `richStr()` shape, rendered as a textarea.
 * `x-rich` is the explicit signal; without it the form falls back to guessing
 * from the description length.
 */
export const richTextField = (title: string, hint?: string): JSONSchemaNode => ({
  type: 'string',
  'x-rich': true,
  title,
  ...(hint ? { 'x-hint': hint } : {}),
});

/**
 * A long-form leaf the site renders with its inline marks — the backend `markedStr()`
 * shape (F-R16-04). `x-marks` puts the Bold / Italic / Highlight toolbar over the
 * textarea; a mirror of the served schema, like every other field here.
 */
export const markedTextField = (title: string, hint?: string): JSONSchemaNode => ({
  ...richTextField(title, hint),
  'x-marks': true,
});

/** A toggle. */
/**
 * The longest quote a testimonial can be ticked for the home page with (B-R9-02).
 * A MIRROR of be-svet's `quotes.HomeMaxChars`, which the server enforces and serves
 * on `placements.home` as `x-length-cap`; this copy is only the offline fallback.
 */
export const HOME_QUOTE_MAX_CHARS = 220;

export const boolField = (title: string, hint?: string): JSONSchemaNode => ({
  type: 'boolean',
  title,
  ...(hint ? { 'x-hint': hint } : {}),
});

/** A whole-number leaf (session counts, orders). */
export const intField = (title: string, hint?: string): JSONSchemaNode => ({
  type: 'integer',
  title,
  ...(hint ? { 'x-hint': hint } : {}),
});

/**
 * A decimal leaf — money and coordinates (backend `nullableNumber()`). Separate
 * from `intField` because a latitude is not a count, and because an amount field
 * that silently rounds is a money bug.
 */
export const numberField = (title: string, hint?: string): JSONSchemaNode => ({
  type: 'number',
  title,
  ...(hint ? { 'x-hint': hint } : {}),
});

/**
 * A fixed group of related leaves rendered as a sub-form — the backend's
 * `obj({…})`. Used for the fundraising singleton's year and match groups, where
 * flattening `year_two.goal` to `year_two_goal` would lose the grouping the
 * editor actually thinks in.
 */
export const objectField = (
  title: string,
  properties: Record<string, JSONSchemaNode>,
  hint?: string,
): JSONSchemaNode => ({
  type: 'object',
  title,
  ...(hint ? { 'x-hint': hint } : {}),
  properties,
});

/**
 * An offering's `rates` (round 13, B-R13-14), mirroring be-svet
 * `offeringRatesSchema`: the Standard, Supporter and Sponsor amounts the apply
 * page shows next to each rate's name, in whole US dollars, each one empty while
 * it is not agreed. The community-seminars record has no Sponsor rate, and
 * Community is always from $0, so it has no amount here. The server's $100,000
 * typo guard is never shown.
 */
export const offeringRatesField = (sponsor: boolean): JSONSchemaNode => {
  const amount = (title: string, hint: string): JSONSchemaNode => ({
    ...numberField(title, hint),
    type: ['number', 'null'],
    minimum: 0,
  });
  const properties: Record<string, JSONSchemaNode> = {
    standard: amount('fields.rateStandard', 'fields.rateStandardHint'),
    supporter: amount('fields.rateSupporter', 'fields.rateSupporterHint'),
    ...(sponsor ? { sponsor: amount('fields.rateSponsor', 'fields.rateSponsorHint') } : {}),
  };
  return {
    ...objectField('fields.rates', properties, 'fields.ratesHint'),
    'ui:order': sponsor ? ['standard', 'supporter', 'sponsor'] : ['standard', 'supporter'],
  };
};

/** A plain list of strings (a teacher's languages). */
export const stringList = (title: string, hint?: string): JSONSchemaNode =>
  listOf(title, { type: 'string' }, hint);

/** An ordered list of one repeated leaf shape — e.g. a list of slug references. */
export const listOf = (title: string, item: JSONSchemaNode, hint?: string): JSONSchemaNode => ({
  type: 'array',
  title,
  ...(hint ? { 'x-hint': hint } : {}),
  items: item,
});

/** A bilingual text leaf {en, ar} — the backend `i18n()` shape. */
export const i18nField = (title: string, hint?: string): JSONSchemaNode => ({
  ...I18N,
  title,
  ...(hint ? { 'x-hint': hint } : {}),
});

/** A bilingual long-form leaf — rendered as a textarea (backend `i18nRich()`). */
export const i18nRichField = (title: string, hint?: string): JSONSchemaNode => ({
  ...I18N_RICH,
  title,
  ...(hint ? { 'x-hint': hint } : {}),
});

/**
 * A closed choice with localized labels (backend `enumChoice()`). Use it for any
 * field a public filter reads: free text there silently drops an item from its
 * filter, while a dropdown cannot.
 */
export const enumField = (
  options: VocabOption[],
  title: string,
  hint?: string,
): JSONSchemaNode => ({
  type: 'string',
  title,
  enum: options.map((o) => o.value),
  'x-enum-labels': enumLabels(options),
  ...(hint ? { 'x-hint': hint } : {}),
});

/**
 * A closed choice that also admits `null` — "not established yet" as a member of
 * the vocabulary rather than as a missing key (backend `unsettableEnumChoice()`).
 *
 * Use it where the unset state MEANS something the site acts on, not merely where
 * a field happens to be optional. `survey_results.unit` is the case: a delta whose
 * unit nobody has stated must not publish, so the editor has to be able to say so
 * — and, having picked one by mistake, to take it back. A two-value dropdown gives
 * them the first and not the second.
 */
export const unsettableEnumField = (
  options: VocabOption[],
  title: string,
  unsetLabel: string,
  hint?: string,
): JSONSchemaNode => ({
  ...enumField(options, title, hint),
  type: ['string', 'null'],
  enum: [...options.map((o) => o.value), null],
  'x-unset-label': unsetLabel,
});

/**
 * A single media-asset reference. Mirrors the backend `mediaRef()` (a nullable
 * string holding a media UUID; the public read hydrates a resolved sibling). The
 * `format: 'uuid'` makes `detectWidget` render the asset picker offline, matching
 * the live form — the `*_id` name convention would also catch it, but declaring the
 * format keeps the fallback self-describing.
 */
export const media = (title: string, hint?: string): JSONSchemaNode => ({
  type: 'string',
  format: 'uuid',
  title,
  ...(hint ? { 'x-hint': hint } : {}),
});

/**
 * A string that references another collection by *slug* (backend `refSlug()`), so
 * `detectWidget` renders the reference picker (a live dropdown of that collection's
 * items) instead of a raw text box. The persisted value is the canonical slug.
 */
export const refSlugField = (collection: string, title: string, hint?: string): JSONSchemaNode => ({
  type: 'string',
  'x-ref-slug': collection,
  title,
  ...(hint ? { 'x-hint': hint } : {}),
});

/**
 * A string that references a key-addressed collection by its *key* (backend
 * `refKey()`, `x-ref-ident: "key"`), e.g. a course's `delivery_partner_key` → partners. The
 * backend refuses a key that does not exist (`unknown_ref`), so this is a picker, not
 * free text.
 */
export const refKeyField = (collection: string, title: string, hint?: string): JSONSchemaNode => ({
  ...refSlugField(collection, title, hint),
  'x-ref-ident': 'key',
});

/**
 * A calendar day (backend `isoDate()`): a nullable `YYYY-MM-DD` string rendered as
 * the native date picker. `format: 'date'` is what selects the picker, offline
 * exactly as on the served schema.
 */
export const dateField = (title: string, hint?: string): JSONSchemaNode => ({
  type: ['string', 'null'],
  format: 'date',
  pattern: '^\\d{4}-\\d{2}-\\d{2}$',
  title,
  ...(hint ? { 'x-hint': hint } : {}),
});

/**
 * A list of ISO 3166-1 alpha-2 country codes (backend `countryCodes()`), picked
 * by name in the countries multi-select.
 */
/**
 * One ISO 3166-1 alpha-2 code or null (backend `countryCode()`, round 9): the same
 * countries picker, which reads the string type as "pick one".
 */
export const countryCodeField = (title: string, hint?: string): JSONSchemaNode => ({
  type: ['string', 'null'],
  title,
  pattern: '^[A-Z]{2}$',
  'x-widget': 'countries',
  ...(hint ? { 'x-hint': hint } : {}),
});

export const countriesField = (title: string, hint?: string, maxItems = 100): JSONSchemaNode => ({
  type: 'array',
  title,
  items: { type: 'string', pattern: '^[A-Z]{2}$' },
  uniqueItems: true,
  maxItems,
  'x-widget': 'countries',
  ...(hint ? { 'x-hint': hint } : {}),
});

/** A card-mark choice (backend `cardColorChoice`/`cardIconChoice`, `x-card-marks`). */
export const cardMarkField = (
  mark: 'color' | 'icon',
  title: string,
  hint?: string,
): JSONSchemaNode => ({
  ...enumField(mark === 'color' ? CARD_COLOR : CARD_ICON, title, hint),
  'x-card-marks': mark,
});

/**
 * An offering's optional card picture (backend `cardPictureSchema`, B-R14-03): a
 * media-library image that fills the card's tile in place of the colour and icon,
 * and its alt text, which the save requires once an image is picked (the
 * `required` media gate, so the form names the alt input before the save does).
 */
export const cardPictureField = (hint: string): JSONSchemaNode => ({
  ...objectField(
    'fields.cardPicture',
    {
      image_id: media('fields.cardPictureImage', 'fields.cardPictureImageHint'),
      alt: textField('fields.altText', 'fields.cardPictureAltHint'),
    },
    hint,
  ),
  'ui:order': ['image_id', 'alt'],
  'x-media-gates': [{ media: 'image_id', requires: 'alt', mode: 'required' }],
});

/**
 * An offering's application form (backend `applicationFormSchema()`). Only its
 * outline is restated: `x-application-form` routes it to the form builder
 * (`widgets/ApplicationFormField`), which owns the question shape, and the
 * served schema — the one the save is validated against — wins over this one.
 */
export const applicationFormField = (): JSONSchemaNode => ({
  type: 'object',
  title: 'fields.applicationForm',
  'x-hint': 'fields.applicationFormHint',
  'x-application-form': true,
  properties: { fields: { type: 'array', items: { type: 'object' } } },
});

/**
 * A repeatable fieldset: an array of objects sharing one property shape (backend
 * `arr(obj({…}))`), e.g. a course's sessions or a page's gallery. Renders as the
 * schema-form's add/remove item list.
 */
export const objectArray = (
  title: string,
  properties: Record<string, JSONSchemaNode>,
  hint?: string,
): JSONSchemaNode => ({
  type: 'array',
  title,
  ...(hint ? { 'x-hint': hint } : {}),
  items: { type: 'object', properties },
});

/** A bilingual string list {en:[…], ar:[…]} — the backend `i18nArr()` shape. */
export const i18nStringArray = (title: string, hint?: string): JSONSchemaNode => ({
  type: 'object',
  title,
  ...(hint ? { 'x-hint': hint } : {}),
  properties: {
    en: { type: 'array', title: 'fields.bodyEn', items: { type: 'string' } },
    ar: { type: 'array', title: 'fields.bodyAr', items: { type: 'string' } },
  },
});

/**
 * One block of a typed body union (paragraph | subheading | list | pullquote |
 * aside | figure). Every member field is optional, gated by `type`; only `type` is
 * closed, so an unknown block can never reach a renderer with no case for it —
 * the end-to-end injection-safety guarantee (never raw HTML).
 */
export const bodyBlock = (blockTypes: VocabOption[]): JSONSchemaNode => ({
  type: 'object',
  title: 'fields.bodyBlock',
  properties: {
    type: enumField(blockTypes, 'fields.blockType'),
    text: { type: 'string', title: 'fields.blockText', 'x-hint': 'fields.blockTextHint' },
    items: { type: 'array', title: 'fields.blockItems', items: { type: 'string' } },
    attribution: {
      type: 'string',
      title: 'fields.blockAttribution',
      'x-hint': 'fields.blockAttributionHint',
    },
    caption: { type: 'string', title: 'fields.blockCaption' },
  },
});

/** Localized body: two parallel block arrays {en:[block], ar:[block]}. */
export const localizedBody = (blockTypes: VocabOption[]): JSONSchemaNode => ({
  type: 'object',
  title: 'fields.body',
  'x-hint': 'fields.bodyHint',
  properties: {
    en: { type: 'array', title: 'fields.bodyEn', items: bodyBlock(blockTypes) },
    ar: { type: 'array', title: 'fields.bodyAr', items: bodyBlock(blockTypes) },
  },
});

/**
 * The console's collection registry.
 *
 * ── On the `docSchema` field, and its `ui:order` / `ui:groups` ──────────────
 *
 * These are the **fallback**, not the source of truth. `ContentEditorPage`
 * resolves `schemaQuery.data?.[entityType] ?? cfg.docSchema`, so whenever
 * `GET /admin/collection-schemas` answers, the backend's schema wins outright —
 * properties, order and grouping alike. What is written here is what an editor
 * sees when that fetch fails, and nothing more.
 *
 * So the six group sets below (`courses`, `works`, `svet-projects`,
 * `press-items`, `testimonials`, `student-origins`) are mirrored from
 * `be-svet/internal/docschema/schemas.go` → `formGroups()`, which is where they
 * take effect. Change one and change the other; the backend's
 * `TestUIGroupsNameRealProperties` is what catches a group naming a field the
 * schema no longer has, and it can only see its own side of the wire.
 */
/** A fundraising year's key (be-svet `yearKey()`): a lower-case slug. */
const YEAR_KEY = '^[a-z0-9]+(?:-[a-z0-9]+)*$';
/** The match's year: a year key, or empty for no match. */
const YEAR_KEY_OR_EMPTY = '^$|^[a-z0-9]+(?:-[a-z0-9]+)*$';
/** The caps be-svet enforces on a year (`maxYearFigures`, `maxYearRows`). */
export const MAX_YEAR_FIGURES = 3;
export const MAX_YEAR_ROWS = 20;

/**
 * One fundraising year (round 9, D-161), mirroring be-svet `fundraisingYearSchema`:
 * the period, the goal and what is raised, up to three figures, a highlight, and
 * the budget rows. The rows nest inside the year, so they get the same add, remove
 * and reorder controls as every list. `x-budget-total` puts "Total $X of goal $Y"
 * under the year, amber when they differ: the server saves a mismatch and warns,
 * so the editor sees it while typing rather than after the save.
 */
function fundraisingYearField(): JSONSchemaNode {
  return {
    ...objectField('fields.fundraisingYear', {
      key: { ...textField('fields.yearKey', 'fields.yearKeyHint'), pattern: YEAR_KEY, maxLength: 40 },
      label: i18nField('fields.yearLabel', 'fields.yearLabelHint'),
      period_start: dateField('fields.periodStart', 'fields.periodStartHint'),
      period_end: dateField('fields.periodEnd', 'fields.periodEndHint'),
      status: enumField(FUNDRAISING_YEAR_STATUS, 'fields.yearStatus', 'fields.yearStatusHint'),
      goal: numberField('fields.goal', 'fields.goalHint'),
      raised: numberField('fields.raised', 'fields.raisedHint'),
      figures: {
        ...listOf(
          'fields.yearFigures',
          objectField('fields.yearFigure', {
            number: textField('fields.figureNumber', 'fields.figureNumberHint'),
            label: i18nField('fields.figureLabel', 'fields.figureLabelHint'),
            line: i18nField('fields.figureLine', 'fields.figureLineHint'),
          }),
          'fields.yearFiguresHint',
        ),
        maxItems: MAX_YEAR_FIGURES,
      },
      highlight: i18nField('fields.yearHighlight', 'fields.yearHighlightHint'),
      rows: {
        ...listOf(
          'fields.budgetRows',
          objectField('fields.budgetRow', {
            name: i18nField('fields.rowName', 'fields.rowNameHint'),
            description: i18nField('fields.rowDescription', 'fields.rowDescriptionHint'),
            amount: numberField('fields.rowAmount', 'fields.rowAmountHint'),
            is_staff: boolField('fields.rowIsStaff', 'fields.rowIsStaffHint'),
          }),
          'fields.budgetRowsHint',
        ),
        maxItems: MAX_YEAR_ROWS,
      },
    }),
    'x-budget-total': { rows: 'rows', amount: 'amount', goal: 'goal' },
  };
}

export const COLLECTIONS: CollectionConfig[] = [
  {
    key: 'courses',
    orderable: true,
    entity: 'course',
    navKey: 'courses',
    label: 'collections.courses.label',
    singularLabel: 'collections.courses.singular',
    identType: 'slug',
    hasSeo: true,
    // Both facets are indexed as `doc->>'…'` expressions by be-svet migration
    // 0022; declaring one here that the backend does not index is a silent seq
    // scan on every public list request.
    // The status facet filters on the EFFECTIVE status (round 8): be-svet applies it to the
    // status it derives from the dates, so the filter and the column below always agree.
    facets: [
      { key: 'status', label: 'content.effectiveStatus', type: 'enum', options: COURSE_STATUS },
      { key: 'format', label: 'fields.courseFormat', type: 'enum', options: COURSE_FORMAT },
    ],
    effectiveStatusColumn: true,
    docSchema: {
      type: 'object',
      $defs: SCHEMA_DEFS,
      // Seventeen fields in one stack is the form people abandon at field nine.
      // The seams are the questions a course page actually answers, in the order
      // someone setting one up already thinks in (PHASE 13).
      'ui:groups': [
        {
          title: 'forms.groups.whatItIs',
          fields: ['short_title', 'format', 'card_location', 'summary', 'card_summary', 'body'],
        },
        // Round 8 (D-143): the site works the status out from these dates; the override
        // and the stored status sit with them because they only matter in that light.
        {
          title: 'forms.groups.whenItRuns',
          description: 'forms.groups.whenItRunsHint',
          fields: [
            'application_deadline',
            'start_date',
            'end_date',
            'status_override',
            'status',
            'schedule',
            'session_count',
            'timezone',
          ],
        },
        {
          title: 'forms.groups.whoItIsFor',
          fields: ['location', 'age_range', 'partner_keys', 'delivery_partner_key'],
        },
        // What the parent's release carries (round 13, B-R13-12).
        {
          title: 'forms.groups.parentRelease',
          description: 'forms.groups.parentReleaseHint',
          fields: ['in_person', 'includes_projects'],
        },
        // No applications switch: the status is the only one (D-77, be-svet
        // migration 0046). "Open now" is what makes a course take applications.
        {
          title: 'forms.groups.cost',
          fields: ['price_line', 'scholarship_line', 'is_free', 'cost_note'],
        },
        {
          title: 'forms.groups.whoTookIt',
          fields: ['students_count', 'student_countries'],
        },
        {
          title: 'forms.groups.cardMark',
          fields: ['card_color', 'card_icon', 'card_picture'],
        },
        {
          title: 'forms.groups.materials',
          fields: ['reading_list', 'photos'],
        },
        {
          title: 'forms.groups.application',
          fields: ['takes_minutes', 'rate_scale', 'rates', 'application_form'],
        },
      ],
      properties: {
        status: enumField(COURSE_STATUS, 'fields.courseStatusStored', 'fields.courseStatusStoredHint'),
        status_override: {
          ...enumField(COURSE_STATUS_OVERRIDE, 'fields.statusOverride', 'fields.statusOverrideHint'),
          default: '',
        },
        application_deadline: dateField('fields.applicationDeadline', 'fields.applicationDeadlineHint'),
        format: enumField(COURSE_FORMAT, 'fields.courseFormat'),
        // Round 14 (B-R14-04): the short place the site's format tag names, "In person, USA".
        card_location: { ...textField('fields.cardLocation', 'fields.cardLocationHint'), maxLength: 40 },
        short_title: { ...textField('fields.courseShortTitle', 'fields.courseShortTitleHint'), maxLength: 30 },
        summary: textField('fields.courseSummary', 'fields.courseSummaryHint'),
        card_summary: { ...textField('fields.cardSummary', 'fields.cardSummaryHint'), maxLength: 140 },
        body: markedTextField('fields.courseBody', 'fields.courseBodyHint'),
        schedule: textField('fields.schedule', 'fields.scheduleHint'),
        start_date: dateField('fields.startDate', 'fields.isoDateHint'),
        end_date: dateField('fields.endDate', 'fields.isoDateHint'),
        session_count: intField('fields.sessionCount'),
        location: textField('fields.location', 'fields.locationHint'),
        in_person: boolField('fields.inPerson', 'fields.inPersonHint'),
        includes_projects: boolField('fields.includesProjects', 'fields.includesProjectsHint'),
        timezone: textField('fields.timezone', 'fields.timezoneHint'),
        age_range: textField('fields.ageRange', 'fields.ageRangeHint'),
        partner_keys: {
          ...listOf('fields.partnerKeys', refKeyField('partners', 'fields.partner'), 'fields.partnerKeysHint'),
          uniqueItems: true,
          maxItems: 6,
        },
        delivery_partner_key: refKeyField(
          'partners',
          'fields.deliveryPartnerKey',
          'fields.deliveryPartnerKeyHint',
        ),
        is_free: boolField('fields.isFree'),
        cost_note: textField('fields.costNote', 'fields.costNoteHint'),
        price_line: textField('fields.priceLine', 'fields.priceLineHint'),
        scholarship_line: textField('fields.scholarshipLine', 'fields.scholarshipLineHint'),
        takes_minutes: intField('fields.takesMinutes', 'fields.takesMinutesHint'),
        students_count: intField('fields.studentsCount', 'fields.studentsCountHint'),
        student_countries: countriesField('fields.studentCountries', 'fields.studentCountriesHint'),
        card_color: cardMarkField('color', 'fields.cardColor', 'fields.cardColorHint'),
        card_icon: cardMarkField('icon', 'fields.cardIcon', 'fields.cardIconHint'),
        card_picture: cardPictureField('fields.cardPictureHint'),
        reading_list: listOf(
          'fields.readingList',
          refSlugField('works', 'fields.work'),
          'fields.readingListHint',
        ),
        photos: objectArray(
          'fields.photos',
          {
            image_id: media('fields.photo'),
            alt: textField('fields.altText'),
            caption: textField('fields.caption'),
            consent_ref: textField('fields.consentRef', 'fields.photoConsentHint'),
          },
          'fields.photosHint',
        ),
        rate_scale: { ...enumField(RATE_SCALE, 'fields.rateScale', 'fields.rateScaleHint'), default: 'course' },
        rates: offeringRatesField(true),
        application_form: applicationFormField(),
      },
    },
  },
  {
    key: 'works',
    orderable: true,
    countryColumn: true,
    entity: 'work',
    navKey: 'works',
    label: 'collections.works.label',
    singularLabel: 'collections.works.singular',
    // Slug-addressed so a course's reading list can reference a work by a handle
    // an editor can read, but NOT SEO-bearing: a work is not a page on this site.
    identType: 'slug',
    hasSeo: false,
    facets: [
      { key: 'category', label: 'fields.workCategory', type: 'enum', options: WORK_CATEGORY },
      { key: 'course', label: 'fields.course', type: 'ref-slug', refCollection: 'courses' },
    ],
    docSchema: {
      type: 'object',
      $defs: SCHEMA_DEFS,
      'ui:groups': [
        {
          title: 'forms.groups.theWork',
          fields: ['category', 'creator', 'year', 'countries', 'country_text', 'course', 'description', 'image_id'],
        },
        {
          title: 'forms.groups.rightsAndLinks',
          description: 'forms.groups.rightsAndLinksHint',
          fields: ['public_domain', 'rights_note', 'free_url', 'external_url'],
        },
      ],
      properties: {
        category: enumField(WORK_CATEGORY, 'fields.workCategory'),
        creator: textField('fields.creator', 'fields.creatorHint'),
        year: textField('fields.year', 'fields.yearHint'),
        // Round 8 (D-147): where the work comes from — ISO countries, or a short free
        // text for a place that is not a country today, which wins when filled in.
        countries: countriesField('fields.workCountries', 'fields.workCountriesHint', 10),
        country_text: { ...textField('fields.workCountryText', 'fields.workCountryTextHint'), maxLength: 40 },
        course: refSlugField('courses', 'fields.course', 'fields.workCourseHint'),
        // Round 8 (D-147): renamed from `note`. The limit binds only when the text
        // changes, so a longer description written before it still saves untouched.
        description: {
          ...textField('fields.workDescription', 'fields.workDescriptionHint'),
          maxLength: 180,
          'x-limit-on-change': true,
        },
        image_id: media('fields.image', 'fields.workImageHint'),
        public_domain: boolField('fields.publicDomain', 'fields.publicDomainHint'),
        rights_note: textField('fields.rightsNote', 'fields.rightsNoteHint'),
        free_url: textField('fields.freeUrl', 'fields.freeUrlHint'),
        external_url: textField('fields.externalUrl', 'fields.externalUrlHint'),
      },
    },
  },
  {
    key: 'svet-projects',
    orderable: true,
    entity: 'svet_project',
    navKey: 'svetProjects',
    label: 'collections.svetProjects.label',
    singularLabel: 'collections.svetProjects.singular',
    identType: 'slug',
    hasSeo: false,
    facets: [],
    docSchema: {
      type: 'object',
      $defs: SCHEMA_DEFS,
      // consent_ref is OPTIONAL (D-76): a project saves and publishes with its
      // content and media alone, so nothing here is declared required.
      // ROUND 8 (PHASE 03, D-154): the client's form, in the client's order, with a
      // hard limit on every new field. The round-3 questions are kept word for word
      // under a read-only "Earlier version" — never moved, never written to — and
      // the site falls back to them where a new field is empty.
      'x-title-max-length': 45,
      'ui:groups': [
        { title: 'forms.groups.whoseProject', fields: ['first_name', 'country', 'course'] },
        { title: 'forms.groups.theCard', fields: ['summary'] },
        { title: 'forms.groups.inspiredBy', fields: ['inspired_by', 'work', 'own_line'] },
        {
          title: 'forms.groups.fourSteps',
          description: 'forms.groups.fourStepsHint',
          fields: ['step_encounter', 'step_discuss', 'step_sit', 'step_act'],
        },
        {
          title: 'forms.groups.inTheirWords',
          description: 'forms.groups.inTheirWordsHint',
          fields: ['quote'],
        },
        { title: 'forms.groups.photos', fields: ['photos'] },
        { title: 'forms.groups.link', fields: ['link_url', 'link_label'] },
        {
          title: 'forms.groups.consent',
          description: 'forms.groups.consentHint',
          fields: ['consent_ref', 'identifiable'],
        },
        {
          title: 'forms.groups.earlierVersion',
          description: 'forms.groups.earlierVersionHint',
          readonly: true,
          fields: ['trigger', 'what_they_did', 'who_it_reached', 'what_happened', 'story', 'photo_id'],
        },
      ],
      properties: {
        first_name: {
          ...textField('fields.firstName', 'fields.projectFirstNameHint'),
          maxLength: 30,
          'x-limit-on-change': true,
        },
        country: textField('fields.country', 'fields.projectCountryOnlyHint'),
        course: {
          ...refSlugField('courses', 'fields.course', 'fields.projectCourseHint'),
          'x-ref-extra': [{ value: 'community-seminars', label: 'fields.projectCourseCommunitySeminars' }],
        },
        summary: { ...richTextField('fields.projectSummary', 'fields.projectSummaryHint'), maxLength: 160 },
        inspired_by: {
          ...enumField(PROJECT_INSPIRED_BY, 'fields.projectInspiredBy', 'fields.projectInspiredByHint'),
          default: 'work',
        },
        work: {
          ...refSlugField('works', 'fields.projectWork', 'fields.projectWorkHint'),
          'x-visible-when': { inspired_by: ['work'] },
          'x-ref-status': 'published',
          'x-ref-preview': 'description',
        },
        own_line: {
          ...textField('fields.projectOwnLine', 'fields.projectOwnLineHint'),
          maxLength: 80,
          'x-visible-when': { inspired_by: ['own_line'] },
        },
        step_encounter: {
          ...markedTextField('fields.projectStepEncounter', 'fields.projectStepEncounterHint'),
          maxLength: 170,
        },
        step_discuss: { ...markedTextField('fields.projectStepDiscuss', 'fields.projectStepDiscussHint'), maxLength: 170 },
        step_sit: { ...markedTextField('fields.projectStepSit', 'fields.projectStepSitHint'), maxLength: 170 },
        step_act: { ...markedTextField('fields.projectStepAct', 'fields.projectStepActHint'), maxLength: 260 },
        quote: {
          ...richTextField('fields.projectQuote', 'fields.projectQuoteWordForWordHint'),
          maxLength: 140,
          'x-limit-on-change': true,
        },
        photos: {
          ...listOf(
            'fields.projectPhotos',
            objectField('fields.photo', {
              image_id: media('fields.photo'),
              alt: textField('fields.altText', 'fields.projectPhotoAltHint'),
              caption: textField('fields.caption', 'fields.optionalHint'),
            }),
            'fields.projectPhotosHint',
          ),
          maxItems: 10,
        },
        // The backend rejects anything that is not https:// — a `javascript:` value
        // here would become an href on a public page. The console states the rule in
        // the hint so the editor learns it before the 422, not from it.
        link_url: textField('fields.projectLinkUrl', 'fields.projectLinkUrlHttpsHint'),
        link_label: {
          ...textField('fields.projectLinkLabel', 'fields.projectLinkLabelShortHint'),
          maxLength: 40,
          'x-limit-on-change': true,
        },
        consent_ref: textField('fields.consentRef', 'fields.storyConsentHint'),
        identifiable: boolField('fields.identifiable', 'fields.projectIdentifiableHint'),
        // Earlier version (read-only): no hints and no limits — the text is what it is.
        trigger: richTextField('fields.projectTrigger'),
        what_they_did: richTextField('fields.whatTheyDid'),
        who_it_reached: textField('fields.whoItReached'),
        what_happened: richTextField('fields.whatHappened'),
        story: richTextField('fields.story'),
        photo_id: media('fields.projectPhoto'),
      },
    },
  },
  {
    key: 'press-items',
    orderable: true,
    entity: 'press_item',
    navKey: 'pressItems',
    label: 'collections.pressItems.label',
    singularLabel: 'collections.pressItems.singular',
    // Key-addressed: a press item links out, it is not a page here (D-07).
    identType: 'key',
    hasSeo: false,
    // Indexed as `doc->>'media_type'` by be-svet migration 0026 and declared a
    // facet on content.PressItems, so the console filters article-vs-podcast
    // server-side (the retired /press/ page grouped by it the same way).
    facets: [{ key: 'media_type', label: 'fields.mediaType', type: 'enum', options: MEDIA_TYPE }],
    docSchema: {
      type: 'object',
      $defs: SCHEMA_DEFS,
      'ui:groups': [
        {
          title: 'forms.groups.theItem',
          fields: ['outlet', 'media_type', 'written_version', 'date', 'short_headline'],
        },
        {
          title: 'forms.groups.links',
          description: 'forms.groups.linksHint',
          fields: ['url', 'archive_url', 'paywalled'],
        },
        {
          title: 'forms.groups.assetsAndRights',
          fields: ['thumbnail_id', 'thumbnail_rights', 'pdf_id', 'reprint_permission'],
        },
        // Round 8 (note 09): the card no longer prints the byline or the pull quote.
        // Kept, editable, in a group that starts closed (mirrors be-svet's served schema).
        {
          title: 'forms.groups.hiddenFields',
          description: 'forms.groups.hiddenFieldsHint',
          collapsed: true,
          fields: ['author', 'quote'],
        },
      ],
      properties: {
        outlet: textField('fields.outlet'),
        media_type: enumField(MEDIA_TYPE, 'fields.mediaType', 'fields.mediaTypeHint'),
        // Round 11 (B-R11-11): the card's foot link reads "Listen or read" / "Watch or
        // read" when it is on. An article is read already, so only the other three offer it.
        written_version: {
          ...boolField('fields.writtenVersion', 'fields.writtenVersionHint'),
          'x-visible-when': { media_type: ['podcast', 'video', 'broadcast'] },
        },
        thumbnail_id: media('fields.thumbnail', 'fields.thumbnailHint'),
        thumbnail_rights: textField('fields.thumbnailRights', 'fields.thumbnailRightsHint'),
        date: dateField('fields.articleDate', 'fields.isoDateHint'),
        short_headline: { ...textField('fields.shortHeadline', 'fields.shortHeadlineHint'), maxLength: 70 },
        author: textField('fields.articleAuthor', 'fields.articleAuthorHint'),
        url: textField('fields.articleUrl', 'fields.articleUrlHint'),
        archive_url: textField('fields.archiveUrl', 'fields.archiveUrlHint'),
        paywalled: boolField('fields.paywalled'),
        pdf_id: media('fields.articlePdf', 'fields.articlePdfHint'),
        reprint_permission: boolField('fields.reprintPermission', 'fields.reprintPermissionHint'),
        quote: {
          type: 'object',
          title: 'fields.pullQuote',
          'x-hint': 'fields.pullQuoteHint',
          properties: {
            text: richTextField('fields.quoteText', 'fields.quoteTextHint'),
            attribution: textField('fields.quoteAttribution'),
            note: textField('fields.quoteNote', 'fields.quoteNoteHint'),
          },
        },
      },
    },
  },

  // ── The key-addressed registers (PHASE 03) ─────────────────────────────────
  //
  // These eleven are what CF-63 asks for: every number and asset the client said
  // "keeps changing" is a field an editor can change, not a constant a developer
  // has to redeploy. Objective O4 — a staff member publishes a figure change in
  // under two minutes, unaided — is measured against these forms, which is why
  // almost every field carries a hint written for a person who has never seen
  // this console before.
  //
  // Each is `identType: 'key'` with `hasSeo: false`: a register item is not a page
  // on this site, and offering a meta-description editor for a record with no URL
  // would be asking for copy nothing can use.
  //
  // None of them declares its own display string as a doc field. The record's own
  // title control IS that string (`be-svet` mirrors it into `doc.<TitleCol>` on
  // write), so declaring it here would give the editor two boxes for one value.
  {
    key: 'partners',
    orderable: true,
    entity: 'partner',
    navKey: 'partners',
    label: 'collections.partners.label',
    singularLabel: 'collections.partners.singular',
    identType: 'key',
    hasSeo: false,
    facets: [],
    docSchema: {
      type: 'object',
      $defs: SCHEMA_DEFS,
      'ui:order': [
        'kind',
        'short_name',
        'url',
        'relationship',
        'logo_id',
        'logo_size',
        'permission_on_file',
        'hero_line',
      ],
      properties: {
        // Round 11 (note 38, B-R11-12): the group the partner sits in on /partners/.
        kind: {
          ...enumField(PARTNER_KIND, 'fields.partnerKind', 'fields.partnerKindHint'),
          default: 'partner',
        },
        // Round 8 (note 08): uploads from this field are trimmed of transparent margins.
        logo_id: { ...media('fields.partnerLogo', 'fields.partnerLogoHint'), 'x-media-trim': 'transparent' },
        short_name: { ...textField('fields.partnerShortName', 'fields.partnerShortNameHint'), maxLength: 40 },
        logo_size: {
          ...enumField(LOGO_SIZE, 'fields.logoSize', 'fields.logoSizeHint'),
          default: 'normal',
        },
        url: textField('fields.partnerUrl', 'fields.partnerUrlHint'),
        // Round 9 (D-184): up to 40 characters (30 in round 8), binding only when the
        // text changes — a longer stored descriptor still saves untouched.
        relationship: {
          ...textField('fields.relationship', 'fields.relationshipHint'),
          maxLength: 40,
          'x-limit-on-change': true,
        },
        // Retired in round 9 (D-184): every partner shows its logo. Kept in the data and
        // hidden from the form; the server preserves it on save.
        permission_on_file: {
          ...boolField('fields.permissionOnFile', 'fields.permissionOnFileHint'),
          'x-hidden': true,
        },
        // ROUND 4 (CF-03/CF-13): the two round-3 placement checkboxes became one
        // dropdown. `featured_on_home` is GONE rather than hidden — the home page
        // shows every published partner now, so the box selected nothing, and a
        // control an editor can tick that changes no page is a defect in its own
        // right. `featured_in_hero` became `hero_line` because the hero makes two
        // different claims ("In partnership with" is a partnership, "Founded by
        // graduates of" is an affiliation) and a checkbox could say a partner was
        // up there without saying which sentence was true of them.
        hero_line: enumField(HERO_LINE, 'fields.heroLine', 'fields.heroLineHint'),
      },
    },
  },
  {
    key: 'testimonials',
    orderable: true,
    placements: true,
    entity: 'testimonial',
    navKey: 'testimonials',
    label: 'collections.testimonials.label',
    singularLabel: 'collections.testimonials.singular',
    identType: 'key',
    hasSeo: false,
    facets: [],
    docSchema: {
      type: 'object',
      $defs: SCHEMA_DEFS,
      // consent_ref is OPTIONAL (D-76), as on svet-projects: a quote saves and
      // publishes without one, so nothing here is declared required.
      // The quote is the record; everything else describes it. Grouped so the form
      // opens on the words rather than, as the served schema's alphabetical key
      // order gave us, on `age` (DEF-19).
      'ui:groups': [
        {
          title: 'forms.groups.theQuote',
          fields: ['kind', 'quote', 'statistic_caption', 'role', 'partner_key', 'anonymous'],
        },
        {
          title: 'forms.groups.context',
          fields: ['courses', 'country', 'country_code', 'age', 'date'],
        },
        {
          title: 'forms.groups.whereItShows',
          description: 'forms.groups.whereItShowsHint',
          fields: ['placements'],
        },
        {
          title: 'forms.groups.portraitAndConsent',
          description: 'forms.groups.consentHint',
          fields: ['portrait_id', 'portrait_consent', 'consent_ref'],
        },
      ],
      properties: {
        // Round 8: what the card switches on — a student's course and country, a
        // partner's role, or a statistic's figure without quotation marks. Round 9
        // (R9-20) adds `parent`: their role and country.
        kind: enumField(TESTIMONIAL_KIND, 'fields.testimonialKind', 'fields.testimonialKindHint'),
        quote: richTextField('fields.quote', 'fields.quoteHint'),
        statistic_caption: {
          ...textField('fields.statisticCaption', 'fields.statisticCaptionHint'),
          'x-visible-when': STATISTIC,
        },
        role: { ...textField('fields.speakerRole', 'fields.speakerRoleHint'), 'x-visible-when': SPEAKER },
        // Round 11 (B-R11-10): the partner a partner's quote speaks for; the site shows
        // its logo in the quote's circle.
        partner_key: {
          ...refKeyField('partners', 'fields.testimonialPartner', 'fields.testimonialPartnerHint'),
          'x-visible-when': PARTNER_SPEAKER,
        },
        // Round 9: a person icon in place of a name and portrait; never a portrait.
        anonymous: {
          ...boolField('fields.anonymous', 'fields.anonymousHint'),
          default: false,
          'x-visible-when': SPEAKER,
        },
        courses: {
          type: 'array',
          title: 'fields.testimonialCourses',
          items: { type: 'string', 'x-ref-slug': 'courses' },
          uniqueItems: true,
          'x-hint': 'fields.testimonialCoursesHint',
        },
        country: { ...textField('fields.speakerCountry', 'fields.speakerCountryHint'), 'x-visible-when': SPEAKER },
        // Round 9: the same country as a code, which draws the card's flag.
        country_code: {
          ...countryCodeField('fields.speakerCountryCode', 'fields.speakerCountryCodeHint'),
          'x-visible-when': SPEAKER,
        },
        age: { ...intField('fields.age'), 'x-visible-when': SPEAKER },
        date: dateField('fields.quoteDate', 'fields.isoDateHint'),
        // Round 8 (R8-15): nothing ticked means it shows nowhere, even when published.
        // Each place has its own order ("Order by place" on the list).
        placements: {
          type: 'object',
          title: 'fields.placements',
          'x-hint': 'fields.placementsHint',
          properties: {
            // B-R9-02: Home takes a quote of at most HOME_QUOTE_MAX_CHARS.
            home: {
              ...boolField('fields.placementHome', 'fields.placementHomeHint'),
              'x-length-cap': { field: 'quote', max: HOME_QUOTE_MAX_CHARS, label: 'fields.quote' },
            },
            course: boolField('fields.placementCourse'),
            impact: boolField('fields.placementImpact'),
            partners: boolField('fields.placementPartners'),
            community_seminars: boolField('fields.placementCommunitySeminars'),
          },
        },
        portrait_id: { ...media('fields.portrait', 'fields.portraitConsentHint'), 'x-visible-when': SPEAKER },
        // Round 9 (R9-20): the site serves the portrait only once this is ticked.
        portrait_consent: {
          ...boolField('fields.portraitConsent', 'fields.portraitConsentConfirmHint'),
          default: false,
          'x-visible-when': SPEAKER,
        },
        consent_ref: textField('fields.consentRef', 'fields.quoteConsentHint'),
      },
    },
  },
  {
    key: 'documents',
    entity: 'document',
    navKey: 'documents',
    label: 'collections.documents.label',
    singularLabel: 'collections.documents.singular',
    identType: 'key',
    // The table is `svet_documents`; the route is `/content/documents`. See `apiKey`.
    apiKey: 'svet_documents',
    hasSeo: false,
    facets: [],
    docSchema: {
      type: 'object',
      $defs: SCHEMA_DEFS,
      properties: {
        pdf_id: media('fields.documentFile', 'fields.documentFileHint'),
        description: textField('fields.documentDescription', 'fields.documentDescriptionHint'),
        category: enumField(DOCUMENT_CATEGORY, 'fields.documentCategory'),
        issued_on: dateField('fields.documentIssuedOn', 'fields.documentIssuedOnHint'),
        file_size_bytes: intField('fields.fileSize', 'fields.fileSizeHint'),
        // Round 9 (R9-21): listed before its file exists, with an optional link.
        coming_soon: {
          ...boolField('fields.documentComingSoon', 'fields.documentComingSoonHint'),
          default: false,
        },
        coming_soon_link_label: {
          ...i18nField('fields.documentComingSoonLinkLabel', 'fields.documentComingSoonLinkLabelHint'),
          'x-visible-when': { coming_soon: [true] },
        },
        coming_soon_link_href: {
          ...textField('fields.documentComingSoonLinkHref', 'fields.documentComingSoonLinkHrefHint'),
          pattern: '^$|^https://[^\\s]+$|^/([^/\\s][^\\s]*)?$',
          'x-visible-when': { coming_soon: [true] },
        },
        // Round 11 (B-R11-09): one report in two versions is one document with a second
        // file — one card on the site with a row per file, each under its own label.
        file_label: {
          ...i18nField('fields.documentFileLabel', 'fields.documentFileLabelHint'),
          default: { en: 'Full report', ar: 'التقرير الكامل' },
        },
        second_pdf_id: media('fields.documentSecondFile', 'fields.documentSecondFileHint'),
        second_file_label: {
          ...i18nField('fields.documentSecondFileLabel', 'fields.documentSecondFileLabelHint'),
          default: { en: 'Short version', ar: 'النسخة المختصرة' },
        },
        second_issued_on: dateField('fields.documentSecondIssuedOn', 'fields.documentSecondIssuedOnHint'),
        second_file_size_bytes: intField('fields.documentSecondFileSize', 'fields.documentSecondFileSizeHint'),
      },
    },
  },
  {
    key: 'impact-metrics',
    orderable: true,
    entity: 'impact_metric',
    navKey: 'impactMetrics',
    label: 'collections.impactMetrics.label',
    singularLabel: 'collections.impactMetrics.singular',
    identType: 'key',
    hasSeo: false,
    // Indexed as a `doc->>'kind'` expression by be-svet migration 0023.
    facets: [{ key: 'kind', label: 'fields.metricKind', type: 'enum', options: METRIC_KIND }],
    docSchema: {
      type: 'object',
      $defs: SCHEMA_DEFS,
      // D-09, and the load-bearing rule of this whole group: a projection must
      // not be publishable as an achievement. The backend rejects a save without
      // it; the form must not let an editor discover that on submit.
      required: ['kind'],
      'ui:order': ['kind', 'mode', 'value', 'compute', 'conflict_courses', 'fallback_value', 'source', 'target_date'],
      properties: {
        kind: enumField(METRIC_KIND, 'fields.metricKind', 'fields.metricKindHint'),
        // Round 9 (D-165): typed shows `value` as written; computed works the number out
        // from the live records, falling back to `fallback_value`, then `value`.
        mode: { ...enumField(METRIC_MODE, 'fields.metricMode', 'fields.metricModeHint'), default: 'typed' },
        value: textField('fields.metricValue', 'fields.metricValueHint'),
        compute: {
          ...enumField(METRIC_COMPUTE, 'fields.metricCompute', 'fields.metricComputeHint'),
          'x-visible-when': { mode: ['computed'] },
        },
        conflict_courses: {
          type: 'array',
          title: 'fields.metricConflictCourses',
          items: { type: 'string', 'x-ref-slug': 'courses' },
          uniqueItems: true,
          'x-hint': 'fields.metricConflictCoursesHint',
          'x-visible-when': { mode: ['computed'], compute: ['conflict_share'] },
        },
        fallback_value: {
          ...textField('fields.metricFallbackValue', 'fields.metricFallbackValueHint'),
          'x-visible-when': { mode: ['computed'] },
        },
        source: textField('fields.metricSource', 'fields.metricSourceHint'),
        target_date: dateField('fields.targetDate', 'fields.targetDateHint'),
      },
    },
  },
  {
    key: 'survey-results',
    // Round 9 (D-168): records tick home and impact, each with its own order.
    placements: true,
    entity: 'survey_result',
    navKey: 'surveyResults',
    label: 'collections.surveyResults.label',
    singularLabel: 'collections.surveyResults.singular',
    identType: 'key',
    hasSeo: false,
    facets: [],
    docSchema: {
      type: 'object',
      $defs: SCHEMA_DEFS,
      // Mirrored from be-svet `formGroups()` (round 9): the averages first, the
      // round-3 single figure only while they are empty, then where it shows.
      'ui:groups': [
        {
          title: 'forms.groups.surveyAverages',
          description: 'forms.groups.surveyAveragesHint',
          fields: ['question_key', 'before_avg', 'after_avg', 'n_both'],
        },
        { title: 'forms.groups.surveyWhoAndWhen', fields: ['cohort', 'period', 'sentence'] },
        {
          title: 'forms.groups.surveySingleFigure',
          description: 'forms.groups.surveySingleFigureHint',
          fields: ['delta', 'unit', 'n', 'instrument', 'timing'],
        },
        {
          title: 'forms.groups.whereItShows',
          description: 'forms.groups.whereItShowsHint',
          fields: ['placements'],
        },
      ],
      properties: {
        question_key: textField('fields.surveyQuestionKey', 'fields.surveyQuestionKeyHint'),
        before_avg: { ...numberField('fields.surveyBeforeAvg', 'fields.surveyBeforeAvgHint'), minimum: 1, maximum: 5 },
        after_avg: { ...numberField('fields.surveyAfterAvg', 'fields.surveyAfterAvgHint'), minimum: 1, maximum: 5 },
        // Never served publicly (be-svet strips it); kept for us.
        n_both: { ...intField('fields.surveyNBoth', 'fields.surveyNBothHint'), minimum: 0 },
        cohort: textField('fields.surveyCohort', 'fields.surveyCohortHint'),
        period: textField('fields.surveyPeriod', 'fields.surveyPeriodHint'),
        sentence: i18nField('fields.surveySentence', 'fields.surveySentenceHint'),
        placements: {
          type: 'object',
          title: 'fields.placements',
          'x-hint': 'fields.surveyPlacementsHint',
          properties: {
            home: boolField('fields.surveyPlacementHome', 'fields.surveyPlacementHomeHint'),
            impact: boolField('fields.placementImpact'),
          },
        },
        delta: numberField('fields.delta', 'fields.deltaHint'),
        unit: unsettableEnumField(
          SURVEY_UNIT,
          'fields.surveyUnit',
          'vocab.surveyUnit.unset',
          'fields.surveyUnitHint',
        ),
        n: intField('fields.sampleSize', 'fields.sampleSizeHint'),
        instrument: textField('fields.instrument', 'fields.instrumentHint'),
        timing: textField('fields.timing', 'fields.timingHint'),
      },
    },
  },
  {
    key: 'survey-cohorts',
    // Round 13 (B-R13-03): one cohort's report figures. Impact groups survey results
    // by `cohort` and states each cohort's overall from here.
    entity: 'survey_cohort',
    navKey: 'surveyCohorts',
    label: 'collections.surveyCohorts.label',
    singularLabel: 'collections.surveyCohorts.singular',
    identType: 'key',
    hasSeo: false,
    facets: [],
    docSchema: {
      type: 'object',
      $defs: SCHEMA_DEFS,
      // Mirrored from be-svet `formGroups()`.
      'ui:groups': [
        {
          title: 'forms.groups.surveyCohortCourse',
          description: 'forms.groups.surveyCohortCourseHint',
          fields: ['cohort', 'dates', 'students_accepted', 'sessions', 'full_scholarships_pct', 'fee_revenue_usd'],
        },
        {
          title: 'forms.groups.surveyCohortSurvey',
          description: 'forms.groups.surveyCohortSurveyHint',
          fields: ['students_surveyed', 'countries', 'completed_both', 'overall_before', 'overall_after'],
        },
      ],
      properties: {
        cohort: textField('fields.surveyCohortName', 'fields.surveyCohortNameHint'),
        dates: textField('fields.surveyCohortDates', 'fields.surveyCohortDatesHint'),
        students_accepted: { ...intField('fields.surveyCohortAccepted'), minimum: 0 },
        sessions: { ...intField('fields.surveyCohortSessions'), minimum: 0 },
        full_scholarships_pct: {
          ...intField('fields.surveyCohortScholarships', 'fields.surveyCohortScholarshipsHint'),
          minimum: 0,
          maximum: 100,
        },
        fee_revenue_usd: { ...numberField('fields.surveyCohortRevenue'), minimum: 0 },
        students_surveyed: { ...intField('fields.surveyCohortSurveyed'), minimum: 0 },
        countries: { ...intField('fields.surveyCohortCountries', 'fields.surveyCohortCountriesHint'), minimum: 0 },
        completed_both: { ...intField('fields.surveyCohortBoth'), minimum: 0 },
        overall_before: {
          ...numberField('fields.surveyCohortOverallBefore', 'fields.surveyCohortOverallBeforeHint'),
          minimum: 1,
          maximum: 5,
        },
        overall_after: {
          ...numberField('fields.surveyCohortOverallAfter', 'fields.surveyCohortOverallAfterHint'),
          minimum: 1,
          maximum: 5,
        },
      },
    },
  },
  {
    key: 'research-citations',
    entity: 'research_citation',
    navKey: 'researchCitations',
    label: 'collections.researchCitations.label',
    singularLabel: 'collections.researchCitations.singular',
    identType: 'key',
    hasSeo: false,
    facets: [],
    docSchema: {
      type: 'object',
      $defs: SCHEMA_DEFS,
      properties: {
        source_name: textField('fields.sourceName', 'fields.sourceNameHint'),
        url: textField('fields.citationUrl', 'fields.citationUrlHint'),
        supports_claim: boolField('fields.supportsClaim', 'fields.supportsClaimHint'),
      },
    },
  },
  {
    key: 'student-origins',
    entity: 'student_origin',
    navKey: 'studentOrigins',
    label: 'collections.studentOrigins.label',
    singularLabel: 'collections.studentOrigins.singular',
    identType: 'key',
    hasSeo: false,
    facets: [],
    docSchema: {
      type: 'object',
      $defs: SCHEMA_DEFS,
      // There is no city field here, and there must never be one. Some of these
      // students are in situations where pinpointing a city could identify or
      // endanger them, and a nullable city control is an invitation to fill it in.
      'ui:groups': [
        {
          title: 'forms.groups.thePlace',
          fields: ['kind', 'code', 'non_iso'],
        },
        {
          title: 'forms.groups.theNumber',
          fields: ['count', 'period', 'tier'],
        },
        {
          title: 'forms.groups.mapPosition',
          description: 'forms.groups.mapPositionHint',
          fields: ['lat', 'lon'],
        },
      ],
      properties: {
        kind: enumField(ORIGIN_KIND, 'fields.originKind', 'fields.originKindHint'),
        code: textField('fields.countryCode', 'fields.countryCodeHint'),
        count: intField('fields.studentCount', 'fields.studentCountHint'),
        // Round 9 (D-167): the period the count covers.
        period: textField('fields.originPeriod', 'fields.originPeriodHint'),
        // ROUND 3 (R3-53): the tier is what gets published, sitting directly under
        // the count it was derived from. `count` stays as the editor's working
        // figure — dropping it would destroy the only record of where the tiers
        // came from — but a shaded country labelled "1 student" never ships.
        tier: enumField(ORIGIN_TIER, 'fields.originTier', 'fields.originTierHint'),
        lat: numberField('fields.latitude', 'fields.latitudeHint'),
        lon: numberField('fields.longitude', 'fields.longitudeHint'),
        non_iso: boolField('fields.nonIso', 'fields.nonIsoHint'),
      },
    },
  },
  {
    key: 'pricing-tiers',
    entity: 'pricing_tier',
    appliesToColumn: true,
    navKey: 'pricingTiers',
    label: 'collections.pricingTiers.label',
    singularLabel: 'collections.pricingTiers.singular',
    identType: 'key',
    // A fixed set of rungs on the sliding scale: the application form validates a
    // submitted tier against these keys, so adding or deleting one is a change to
    // a form contract rather than routine editing.
    editOnly: true,
    hasSeo: false,
    facets: [],
    docSchema: {
      type: 'object',
      $defs: SCHEMA_DEFS,
      // Round 7 (B-R7-01…03): a tier is scoped to courses (`courses`, "Applies to") and its
      // price is exact, a range (`amount_max`) or open-ended (`or_more`). The backend's
      // tierDocRules refuse the combinations that cannot mean a price, naming the field.
      // Round 13 (B-R13-14): a tier with a `role` takes its amount from each course's (or the
      // one-time seminars') `rates`, and Community is always from $0, so the three amount
      // fields are not used on it. The served schema shows them all, so their hints say so.
      'ui:order': ['offering', 'courses', 'role', 'amount', 'amount_max', 'or_more', 'description'],
      properties: {
        offering: enumField(OFFERING, 'fields.offering', 'fields.offeringHint'),
        courses: listOf(
          'fields.tierCourses',
          refSlugField('courses', 'fields.course'),
          'fields.tierCoursesHint',
        ),
        role: enumField(RATE_ROLE, 'fields.tierRole', 'fields.tierRoleHint'),
        amount: numberField('fields.tierAmount', 'fields.tierAmountHint'),
        amount_max: numberField('fields.tierAmountMax', 'fields.tierAmountMaxHint'),
        or_more: boolField('fields.tierOrMore', 'fields.tierOrMoreHint'),
        description: textField('fields.tierDescription', 'fields.tierDescriptionHint'),
      },
    },
  },
  {
    // Round 9 (D-172): one record per person. The four role ticks are the sections of
    // the people page and the places of "Order by place"; each tick reveals its fields.
    key: 'people',
    placements: true,
    keyFromTitle: true,
    entity: 'person',
    navKey: 'people',
    label: 'collections.people.label',
    singularLabel: 'collections.people.singular',
    identType: 'key',
    hasSeo: false,
    facets: [],
    docSchema: {
      type: 'object',
      $defs: SCHEMA_DEFS,
      'ui:groups': [
        {
          title: 'forms.groups.personListing',
          description: 'forms.groups.personListingHint',
          fields: ['agreed_to_be_listed', 'is_cofounder', 'is_teacher', 'is_board', 'is_advisor'],
        },
        { title: 'forms.groups.personCard', fields: ['role_line', 'portrait_id', 'linkedin_url'] },
        { title: 'forms.groups.personCofounder', fields: ['long_bio'] },
        {
          title: 'forms.groups.personTeacher',
          fields: ['short_bio', 'studied', 'study_highlight', 'teaches', 'voice', 'languages', 'courses'],
        },
        { title: 'forms.groups.personBoard', fields: ['bio', 'affiliation', 'board_role'] },
        {
          title: 'forms.groups.personLegacy',
          description: 'forms.groups.personLegacyHint',
          readonly: true,
          fields: ['year_joined', 'teacher_bio_legacy', 'teacher_role_legacy', 'teacher_portrait_legacy_id'],
        },
      ],
      properties: {
        agreed_to_be_listed: { ...boolField('fields.agreedToBeListed', 'fields.agreedToBeListedHint'), default: false },
        is_cofounder: { ...boolField('fields.isCofounder', 'fields.isCofounderHint'), default: false },
        is_teacher: { ...boolField('fields.isTeacher', 'fields.isTeacherHint'), default: false },
        is_board: { ...boolField('fields.isBoard', 'fields.isBoardPersonHint'), default: false },
        is_advisor: { ...boolField('fields.isAdvisor', 'fields.isAdvisorPersonHint'), default: false },
        role_line: textField('fields.personRoleLine', 'fields.personRoleLineHint'),
        portrait_id: media('fields.portrait', 'fields.personPortraitHint'),
        // Round 16 (F-R16-05): the backend accepts only https on linkedin.com or
        // www.linkedin.com, because the value becomes the href of the site's LinkedIn icon.
        linkedin_url: {
          ...textField('fields.linkedinUrl', 'fields.linkedinUrlHint'),
          pattern: '^$|^https://(www\\.)?linkedin\\.com/[^\\s]*$',
          maxLength: 300,
        },
        long_bio: { ...markedTextField('fields.longBio', 'fields.longBioHint'), 'x-visible-when': COFOUNDER },
        short_bio: {
          ...markedTextField('fields.shortBio', 'fields.shortBioHint'),
          maxLength: 220,
          'x-limit-on-change': true,
          'x-visible-when': TEACHER,
        },
        studied: { ...textField('fields.teacherStudied', 'fields.personStudiedHint'), 'x-visible-when': TEACHER },
        study_highlight: {
          ...textField('fields.studyHighlight', 'fields.studyHighlightHint'),
          'x-visible-when': TEACHER,
        },
        teaches: { ...textField('fields.teacherTeaches', 'fields.personTeachesHint'), 'x-visible-when': TEACHER },
        voice: {
          ...markedTextField('fields.teacherVoice', 'fields.personVoiceHint'),
          maxLength: 450,
          'x-limit-on-change': true,
          'x-visible-when': TEACHER,
        },
        languages: { ...stringList('fields.languages', 'fields.personLanguagesHint'), 'x-visible-when': TEACHER },
        courses: {
          ...listOf('fields.teacherCourses', refSlugField('courses', 'fields.course'), 'fields.personCoursesHint'),
          'x-visible-when': TEACHER,
        },
        bio: { ...markedTextField('fields.personBio', 'fields.personBioHint'), 'x-visible-when-any': BOARD_OR_ADVISOR },
        affiliation: {
          ...textField('fields.affiliation', 'fields.affiliationHint'),
          'x-visible-when-any': BOARD_OR_ADVISOR,
        },
        board_role: {
          ...textField('fields.boardRole', 'fields.boardRoleHint'),
          'x-visible-when-any': BOARD_OR_ADVISOR,
        },
        year_joined: intField('fields.yearJoined', 'fields.personYearJoinedHint'),
        teacher_bio_legacy: richTextField('fields.teacherBioLegacy', 'fields.teacherBioLegacyHint'),
        teacher_role_legacy: textField('fields.teacherRoleLegacy', 'fields.teacherRoleLegacyHint'),
        teacher_portrait_legacy_id: media('fields.teacherPortraitLegacy', 'fields.teacherPortraitLegacyHint'),
      },
    },
  },
  {
    key: 'teachers',
    replacedBy: 'people',
    entity: 'teacher',
    navKey: 'teachers',
    label: 'collections.teachers.label',
    singularLabel: 'collections.teachers.singular',
    identType: 'key',
    hasSeo: false,
    facets: [],
    docSchema: {
      type: 'object',
      $defs: SCHEMA_DEFS,
      properties: {
        bio: markedTextField('fields.teacherBio', 'fields.teacherBioHint'),
        languages: stringList('fields.languages', 'fields.languagesHint'),
        portrait_id: media('fields.portrait', 'fields.portraitHint'),
      },
    },
  },
  {
    key: 'team-members',
    replacedBy: 'people',
    entity: 'team_member',
    navKey: 'teamMembers',
    label: 'collections.teamMembers.label',
    singularLabel: 'collections.teamMembers.singular',
    identType: 'key',
    hasSeo: false,
    facets: [],
    docSchema: {
      type: 'object',
      $defs: SCHEMA_DEFS,
      properties: {
        role: textField('fields.teamRole', 'fields.teamRoleHint'),
        officer_role: textField('fields.officerRole', 'fields.officerRoleHint'),
        year_joined: intField('fields.yearJoined', 'fields.yearJoinedHint'),
        bio: richTextField('fields.teamBio'),
        portrait_id: media('fields.portrait', 'fields.portraitHint'),
      },
    },
  },
  {
    key: 'community-seminars',
    entity: 'community_seminar',
    navKey: 'communitySeminars',
    singleton: true,
    label: 'collections.communitySeminars.label',
    singularLabel: 'collections.communitySeminars.singular',
    identType: 'key',
    hasSeo: false,
    // ONE row, keyed `current` (D-82), on the fundraising pattern: the console hides
    // create and delete; be-svet migration 0046 makes a second row impossible. Its
    // words are the `community-seminars` page copy — this record holds the facts
    // that change: the next dates, the accepting switch, the reading list, photos.
    editOnly: true,
    facets: [],
    docSchema: {
      type: 'object',
      $defs: SCHEMA_DEFS,
      'ui:groups': [
        {
          title: 'forms.groups.nextSessions',
          fields: ['next_sessions', 'accepting_applications', 'timezone'],
        },
        {
          title: 'forms.groups.cost',
          fields: ['price_line', 'scholarship_line'],
        },
        {
          title: 'forms.groups.about',
          fields: ['body'],
        },
        {
          title: 'forms.groups.materials',
          fields: ['reading_list', 'photos'],
        },
        {
          title: 'forms.groups.cardMark',
          fields: ['card_color', 'card_icon', 'card_picture'],
        },
        {
          title: 'forms.groups.application',
          fields: ['rate_scale', 'rates', 'application_form'],
        },
      ],
      properties: {
        next_sessions: objectArray(
          'fields.nextSessions',
          {
            date: dateField('fields.sessionDate', 'fields.isoDateHint'),
            note: textField('fields.sessionNote', 'fields.sessionNoteHint'),
          },
          'fields.nextSessionsHint',
        ),
        accepting_applications: {
          ...boolField('fields.acceptingApplications', 'fields.acceptingApplicationsHint'),
          default: true,
        },
        timezone: textField('fields.timezone', 'fields.seminarTimezoneHint'),
        body: markedTextField('fields.seminarBody', 'fields.seminarBodyHint'),
        reading_list: listOf(
          'fields.readingList',
          refSlugField('works', 'fields.work'),
          'fields.seminarReadingListHint',
        ),
        photos: objectArray(
          'fields.photos',
          {
            image_id: media('fields.photo'),
            alt: textField('fields.altText'),
            caption: textField('fields.caption'),
            consent_ref: textField('fields.consentRef', 'fields.photoConsentHint'),
          },
          'fields.seminarPhotosHint',
        ),
        card_color: cardMarkField('color', 'fields.cardColor', 'fields.seminarCardColorHint'),
        card_icon: cardMarkField('icon', 'fields.cardIcon', 'fields.seminarCardIconHint'),
        card_picture: cardPictureField('fields.seminarCardPictureHint'),
        price_line: textField('fields.priceLine', 'fields.priceLineHint'),
        scholarship_line: textField('fields.scholarshipLine', 'fields.scholarshipLineHint'),
        rate_scale: {
          ...enumField(RATE_SCALE, 'fields.rateScale', 'fields.seminarRateScaleHint'),
          default: 'community-seminar',
        },
        rates: offeringRatesField(false),
        application_form: applicationFormField(),
      },
    },
  },
  {
    key: 'fundraising',
    entity: 'fundraising',
    navKey: 'fundraising',
    label: 'collections.fundraising.label',
    singularLabel: 'collections.fundraising.singular',
    identType: 'key',
    hasSeo: false,
    // ONE row, keyed `current`. This hides create and delete in the console; the
    // guarantee itself is a CHECK constraint in be-svet migration 0023, because
    // hiding a button is not the same as making a second row impossible.
    editOnly: true,
    facets: [],
    docSchema: {
      type: 'object',
      $defs: SCHEMA_DEFS,
      // Round 9 (D-161): the years are a list, each with its own budget rows. The
      // shares, the total and the staff share are calculated by the server on the
      // public read, so none of them is a field here; the console shows the rows'
      // total against the goal as the editor types (`x-budget-total`).
      properties: {
        years: listOf('fields.fundraisingYears', fundraisingYearField(), 'fields.fundraisingYearsHint'),
        match: objectField('fields.match', {
          cap: numberField('fields.matchCap', 'fields.matchCapHint'),
          description: textField('fields.matchDescription', 'fields.matchDescriptionHint'),
          target: { ...textField('fields.matchTarget', 'fields.matchTargetHint'), pattern: YEAR_KEY_OR_EMPTY },
        }),
        rationale: textField('fields.rationale', 'fields.rationaleHint'),
        updated_at: dateField('fields.figuresUpdated', 'fields.figuresUpdatedHint'),
        // The Givebutter widgets the site embeds. They live on this record, not in
        // the website's code, because a widget is built, renamed and retired in
        // Givebutter and the site has to follow that without a developer. The
        // hosted address is the one that must stay filled in — it is the donate
        // path that survives a blocked script (docs/08 § 1.2).
        givebutter: objectField(
          'fields.givebutter',
          {
            account_id: externalIdField('fields.givebutterAccount', 'fields.givebutterAccountHint'),
            inline_widget_id: externalIdField('fields.givebutterInline', 'fields.givebutterInlineHint'),
            overlay_widget_id: externalIdField('fields.givebutterOverlay', 'fields.givebutterOverlayHint'),
            hosted_url: textField('fields.givebutterHosted', 'fields.givebutterHostedHint'),
          },
          'fields.givebutterHint',
        ),
        // No progress_widget_id: the Support goal bar reads Givebutter's campaign
        // figure through be-svet since B-R12-14, and the field is retired (DEF-29).
      },
    },
  },
];

export function getCollection(key: string): CollectionConfig | undefined {
  return COLLECTIONS.find((c) => c.key === key);
}

/**
 * The admin query key for a doc facet. The admin list endpoint owns `status` for
 * the publication status (draft / scheduled / published / archived), and a
 * collection may legitimately declare a doc facet of the same name — a course's
 * editorial status is not its publication status — so doc facets are namespaced.
 * Mirrors `be-svet`'s `admin.FacetParamPrefix`, which the server also publishes
 * as `admin_facet_param_prefix` in `GET /admin/capabilities`.
 *
 * The PUBLIC list has no collision and keeps the bare `?status=open` form.
 */
export const ADMIN_FACET_PARAM_PREFIX = 'doc.';

export function facetParam(facet: string): string {
  return `${ADMIN_FACET_PARAM_PREFIX}${facet}`;
}

/**
 * Map a backend entity name (`field_note`, `course`) to its console route segment.
 * Derived from the registry rather than a second hand-kept table, so a collection
 * can never be reachable in the sidebar but unreachable from a search hit — and
 * never naively pluralized (`site_asset` → `site-assets`, not `site_assets`).
 */
export function collectionKeyForEntity(entity: string): string | undefined {
  return COLLECTIONS.find((c) => c.entity === entity)?.key;
}

/**
 * Map a key as the BACKEND spells it — a dashboard `content` entry, a table name — to the
 * collection it belongs to.
 *
 * Two steps, in this order. An explicit `apiKey` wins, because it was written down for
 * exactly this; otherwise underscores become hyphens, which is the convention the other
 * fifteen collections satisfy without anybody stating it. An unknown key resolves to
 * `undefined` and its caller renders a non-navigable card, which is the correct defensive
 * default for a collection the console has never heard of — the QA F-11 defect was not that
 * default, it was a key that was not unknown, merely spelled differently on the two sides.
 */
export function collectionForApiKey(apiKey: string): CollectionConfig | undefined {
  const explicit = COLLECTIONS.find((c) => c.apiKey === apiKey);
  if (explicit) return explicit;
  const key = apiKey.replace(/_/g, '-');
  return COLLECTIONS.find((c) => c.key === key);
}

/**
 * The console route for a backend content key, or `null` when there is no page to open.
 *
 * `pages` and `product_families` are not generic content collections — they have dedicated
 * routes and no registry entry — so they are named here. Everything else is the registry,
 * through `collectionForApiKey`. It lives beside the registry rather than in the dashboard
 * because the dashboard is not the only surface that will ever want it, and because the
 * mapping is a property of the collection set.
 */
export function routeForApiKey(apiKey: string): string | null {
  if (apiKey === 'pages') return '/pages';
  if (apiKey === 'product_families' || apiKey === 'product-families') return '/families';
  const collection = collectionForApiKey(apiKey);
  return collection ? `/content/${collection.key}` : null;
}
